import { useCallback, useEffect, useMemo, useState } from "react"

import { App, Flex, Space, Typography } from "antd"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate, useSearchParams } from "react-router-dom"

import EmptyComponent from "../../../Components/Empty"
import Filters from "../../../Components/Filters"
import ManageColumn from "../../../Components/ManageColumns"
import Table from "../../../Components/Table"
import { getBaseColumns } from "../../../Components/Table/baseColumns"
import { serializeData } from "../../../Utils/utils"
import { useUpdatePreferences } from "../../User/hooks/useUpdatePreferences"
import RequestInfoModal from "./components/RequestInfoModal/RequestInfoModal"
import { getActionColumn, getColumns, getDefaultColumn } from "./components/Table/columns"
import Creators, {
  REQUESTS_VERIFI_INFORM_PREFERENCES_VERSION,
  selectAreFiltersDefault
} from "./reducer"

import "./styles.less"

const { Text } = Typography

const pageSize = 50

const pinnedColumns = ["Status"]

const RequestsVerifiInformPage = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const openRequestId = params.get("open_request")
  const [pageNumber, setPageNumber] = useState(1)
  const [ordering, setOrdering] = useState(null)

  const { notification } = App.useApp()

  const notificationConfig = {
    closeIcon: <Text>Dismiss</Text>,
    style: { minWidth: "max-content", width: 475 }
  }

  const businessAccountId = useSelector((state) => state.auth.userInfo.current_business_account_id)
  const isMobile = useSelector((state) => state.nav.isMobile)
  const visibleFiltersAdmin = useSelector((state) => state.requestsVerifiInform.visibleFiltersAdmin)
  const defaultColumnsAdmin = useSelector((state) => state.requestsVerifiInform.defaultColumnsAdmin)
  const columnsAdmin = useSelector((state) => state.requestsVerifiInform.columnsAdmin)
  const fetching = useSelector((state) => state.requestsVerifiInform.fetching)
  const result = useSelector((state) => state.requestsVerifiInform.result)
  const filters = useSelector((state) => state.requestsVerifiInform.filters)
  const filterOptions = useSelector((state) => state.requestsVerifiInform.filterOptions)
  const isFiltersDefault = useSelector(selectAreFiltersDefault)

  const { count, results } = result || {}
  const myColumns = columnsAdmin
  const allFilters = defaultColumnsAdmin
  const visibleFilters = visibleFiltersAdmin

  const myColumnsLength = myColumns.filter(({ active }) => active).length
  const myColumnsHash = myColumns
    .filter(({ active }) => active)
    .map(({ field_id }) => field_id)
    .join("")

  const preferencesData = useMemo(
    () => ({
      version: REQUESTS_VERIFI_INFORM_PREFERENCES_VERSION,
      filters,
      columnsAdmin,
      visibleFiltersAdmin
    }),
    [filters, columnsAdmin, visibleFiltersAdmin]
  )
  useUpdatePreferences("requests-verifi-inform", preferencesData)

  const updateData = () => {
    const serializeFilters = serializeData(filters)
    const columns = myColumns.filter(({ active }) => active).map(({ field_id }) => field_id)
    const data = {
      page: pageNumber,
      page_size: pageSize,
      ordering,
      ...serializeFilters,
      ...(columns.length && {
        columns: ["id", "status", "business_account", ...columns].join(",")
      })
    }
    dispatch(Creators.getRequestsVerifiInformRequest({ ...data }))
  }

  useEffect(() => {
    document.title = "Requests Verifi Inform - Chargeback"
    updateData()
  }, [businessAccountId, ordering, pageNumber, filters, myColumnsLength])

  const tableSettings = useMemo(
    () => ({
      scroll: {
        y: "calc(100vh - 300px)",
        scrollToFirstRowOnChange: true
      },
      pagination: {
        total: count,
        current: pageNumber,
        pageSize: pageSize
      },
      locale: {
        emptyText: <EmptyComponent type="requests_verifi_inform_admin" />
      }
    }),
    [myColumnsLength, count, pageNumber]
  )

  const handleChangeStatus = useCallback(async (id, data) => {
    return new Promise((resolve, reject) => {
      dispatch(
        Creators.updateRequestVerifiInformStatusRequest({
          id,
          ...data,
          onSuccess: (result) => {
            notification.success({
              message: "Request status has been changed",
              ...notificationConfig
            })
            resolve(result)
          },
          onError: (errors) => {
            notification.error({
              message: "Failed to update status",
              ...notificationConfig
            })
            reject(errors)
          }
        })
      )
    })
  }, [])

  const getTableColumns = useMemo(() => {
    let allColumns = {
      ...getBaseColumns({ onSelectRow: handleSelectRow, isAdmin: true }),
      ...getColumns({ onSelectRow: handleSelectRow, handleChangeStatus })
    }
    return [
      allColumns["status"],
      ...myColumns
        .filter(({ active }) => active)
        .map((data) => allColumns?.[data.field_id] || getDefaultColumn({ ...data })),
      getActionColumn({ onSelectRow: handleSelectRow })
    ]
  }, [myColumnsHash])

  const handleChangeTable = useCallback((pagination, filters, sorter) => {
    let ordering = null
    if (sorter) {
      if (Array.isArray(sorter)) {
        ordering = sorter
          ?.map(({ field, order }) => (order === "ascend" ? field : `-${field}`))
          .join(",")
      } else {
        if (sorter.order) {
          ordering = sorter.order === "ascend" ? sorter.field : `-${sorter.field}`
        }
      }
    }
    setPageNumber(pagination.current)
    setOrdering(ordering)
  }, [])

  const handleChangeFilter = useCallback((newData, type) => {
    if (type === "changeVisible") {
      dispatch(Creators.requestsVerifiInformSetProp("visibleFiltersAdmin", newData))
    } else {
      dispatch(Creators.requestsVerifiInformSetProp("filters", newData))
      setPageNumber(1)
    }
  }, [])

  const handleChangeColumns = useCallback((newColumns) => {
    dispatch(Creators.requestsVerifiInformSetProp("columnsAdmin", newColumns))
  }, [])

  function handleSelectRow(data, type) {
    if (type === "status_update") {
    }
    if (type === "view_request") {
      params.set("open_request", data.id)
      setParams(params)
    }
    if (type === "business_account") {
      const business_account_id = data?.business_account?.id
      if (business_account_id) {
        window.open(
          `${process.env.REACT_APP_BASE_URI}/business_settings/enrollment?${new URLSearchParams({ business_account_id })}`
        )
      }
    }
  }

  return (
    <div className="requests-page">
      <Flex gap={32} align="center" justify="space-between" style={{ margin: "0 0 20px" }}>
        <Filters
          visibleFilters={visibleFilters}
          filterOptions={filterOptions}
          allFilters={allFilters}
          filters={filters}
          onChangeFilter={handleChangeFilter}
          filtersType={"verifi_inform"}
          showClearBtn={!isFiltersDefault}
          onClearFilters={() => dispatch(Creators.clearRequestsVerifiInformFilters())}
        />

        <Space style={{ flexShrink: 0 }}>
          <ManageColumn
            myColumns={myColumns}
            onChangeColumns={handleChangeColumns}
            pinnedColumns={pinnedColumns}
          />
        </Space>
      </Flex>

      <Table
        columns={getTableColumns}
        loading={fetching}
        dataSource={results}
        scroll={tableSettings.scroll}
        onChange={handleChangeTable}
        pagination={tableSettings.pagination}
        showSizeChanger={false}
        locale={tableSettings.locale}
      />
      <RequestInfoModal />
    </div>
  )
}

export default RequestsVerifiInformPage
